import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import 'dayjs/locale/pl';

import TrainerLayout from '../TrainerLayout';
import { TrainerContext } from '../../../context/TrainerContext';
import { CalendarProvider as LegacyCalendarProvider } from '../../../context/CalendarContext';

import {
    Calendar,
    CalendarEvent,
    useCalendarNavigation,
    useCalendarSelection,
    useResponsiveCalendar,
} from '../../calendar';
import '../../calendar/styles/index.css';

import { useCalendarData } from '../../../hooks/calendar/useCalendarData';

import PendingRequestsModal from '../PendingRequestsModal';
import BookTrainingModal from '../BookTrainingModal';
import DeleteConfirmationModal from '../DeleteConfirmationModal';
import EditTrainingModal from '../EditTrainingModal';
import TrainingDetailsModal from './modals/TrainingDetailsModal';
import GroupTrainingDetailsModal from './modals/GroupTrainingDetailsModal';
import EditTemporaryLockModal from './modals/EditTemporaryLockModal';
import EditTrainingLockModal from './modals/EditTrainingLockModal';

import '../../../styles/TrainerCalendar.css';

dayjs.extend(isoWeek);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

// ============================================
// Trainer Calendar Controls Component
// ============================================
const TrainerCalendarControls = ({
                                     trainers,
                                     selectedTrainerId,
                                     onTrainerChange,
                                     isLockMode,
                                     selectedLockSlotsCount,
                                     onLockModeToggle,
                                     onSaveLock,
                                     onCancelLock,
                                     isMobile = false,
                                 }) => {
    const { t } = useTranslation('calendar');
    // Use mobile-calendar-controls class on mobile to avoid display:none
    const containerClass = isMobile ? 'mobile-calendar-controls' : 'calendar-controls';

    return (
        <div className={containerClass}>
            <div className="controls-row">
                <div className="trainer-picker">
                    <label>{t('trainerCal.selectTrainerLabel')}</label>
                    <select
                        value={selectedTrainerId || ''}
                        onChange={(e) => onTrainerChange(e.target.value)}
                    >
                        <option value="">{t('trainerCal.selectOption')}</option>
                        {trainers.map((tr) => (
                            <option key={tr.id} value={tr.id}>
                                {tr.firstName} {tr.lastName}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="lock-mode-controls">
                    {isLockMode ? (
                        <>
                            <button className="lock-save-btn" onClick={onSaveLock}>
                                {t('lock.saveLocksCount', { count: selectedLockSlotsCount })}
                            </button>
                            <button className="lock-cancel-btn" onClick={onCancelLock}>
                                {t('common:cancel')}
                            </button>
                            {!isMobile && (
                                <span className="lock-mode-hint">
                                    {t('lock.lockModeHint')}
                                </span>
                            )}
                        </>
                    ) : (
                        <button className="lock-mode-btn" onClick={onLockModeToggle}>
                            {t('lock.lockMode')}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

// ============================================
// Selection Placeholder Component
// ============================================
const SelectionPlaceholder = ({ isMobileView = false }) => {
    const { t } = useTranslation('calendar');
    return (
        <div className={isMobileView ? 'mobile-calendar-placeholder' : 'calendar-placeholder'}>
            <div className={isMobileView ? 'mobile-no-trainer' : 'no-trainer'}>
                <div className={isMobileView ? 'mobile-no-trainer-icon' : 'no-trainer-icon'}>
                    👤
                </div>
                <div className={isMobileView ? 'mobile-no-trainer-text' : 'no-trainer-text'}>
                    {t('trainerCal.selectTrainerToView')}
                </div>
            </div>
        </div>
    );
};

// ============================================
// Lock Mode Booking Bar Component
// ============================================
const LockModeBar = ({
                         selectedSlots,
                         onClearSelection,
                         onSaveLock,
                         isSaving,
                     }) => {
    const { t } = useTranslation('calendar');
    if (selectedSlots.length === 0) return null;

    return (
        <div className="lock-mode-bar">
            <div className="lock-mode-bar__info">
                <span className="lock-mode-bar__count">
                    {t('lock.locksSelected')} <strong>{selectedSlots.length}</strong>
                </span>
            </div>
            <div className="lock-mode-bar__actions">
                <button
                    className="lock-mode-bar__clear"
                    onClick={onClearSelection}
                    disabled={isSaving}
                >
                    {t('clearSelection')}
                </button>
                <button
                    className="lock-mode-bar__submit"
                    onClick={onSaveLock}
                    disabled={isSaving}
                >
                    {isSaving ? t('lock.saving') : t('lock.saveLocks')}
                </button>
            </div>
        </div>
    );
};

// ============================================
// Trainer Calendar Content Component
// ============================================
const TrainerCalendarContent = ({
                                    // Data
                                    confirmedTrainings,
                                    pendingBookings,
                                    groupTrainings,
                                    lockedSlots,
                                    trainers,
                                    services,
                                    halls,
                                    workingHours,
                                    schoolSettings,
                                    // Selected state
                                    selectedTrainer,
                                    // Handlers
                                    onTrainerChange,
                                    onWeekChange,
                                    // Lock mode
                                    isLockMode,
                                    selectedLockSlots,
                                    setSelectedLockSlots,
                                    onLockModeToggle,
                                    onSaveLock,
                                    onCancelLock,
                                    // Refresh
                                    refreshCalendar,
                                    // Actions
                                    deleteTraining,
                                    deleteLock,
                                    lockTimeSlots,
                                    // Loading
                                    isLoading,
                                }) => {
    const { t } = useTranslation(['calendar', 'common']);
    const { shouldUseMobileView } = useResponsiveCalendar();
    const { currentWeekStart, goToNextWeek, goToPrevWeek } = useCalendarNavigation();
    const { selectedSlots, toggleSlot, clearSelection, isSlotSelected } = useCalendarSelection();
    const [calendarSearchParams, setCalendarSearchParams] = useSearchParams();

    const [selectedDay, setSelectedDay] = useState(() => dayjs());

    // Sync selectedDay with currentWeekStart when week changes
    useEffect(() => {
        const weekStart = currentWeekStart;
        const weekEnd = currentWeekStart.add(6, 'day');

        // If selectedDay is outside the current week, reset to first day of week
        if (selectedDay.isBefore(weekStart, 'day') || selectedDay.isAfter(weekEnd, 'day')) {
            setSelectedDay(weekStart);
        }
    }, [currentWeekStart, selectedDay]);

    // Modal states
    const [pendingModalOpen, setPendingModalOpen] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [bookingModalOpen, setBookingModalOpen] = useState(false);
    const [selectedBookingSlot, setSelectedBookingSlot] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [trainingToDelete, setTrainingToDelete] = useState(null);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [trainingToEdit, setTrainingToEdit] = useState(null);
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [selectedTrainingDetails, setSelectedTrainingDetails] = useState(null);
    const [groupDetailsModalOpen, setGroupDetailsModalOpen] = useState(false);
    const [selectedGroupTraining, setSelectedGroupTraining] = useState(null);
    const [editLockModalOpen, setEditLockModalOpen] = useState(false);
    const [lockToEdit, setLockToEdit] = useState(null);
    const [editTempLockModalOpen, setEditTempLockModalOpen] = useState(false);
    const [tempLockToEdit, setTempLockToEdit] = useState(null);

    const isMobileView = shouldUseMobileView();

    // Auto-open pending requests modal when ?pendingTime= param is present
    useEffect(() => {
        const pendingTimeParam = calendarSearchParams.get('pendingTime');
        const dateParam = calendarSearchParams.get('date');
        if (pendingTimeParam && dateParam && selectedTrainer) {
            const startTime = dayjs(`${dateParam}T${pendingTimeParam}`);
            setSelectedSlot({
                startTime: startTime,
                endTime: startTime.add(15, 'minute'),
            });
            setPendingModalOpen(true);
            // Clear pendingTime param to avoid re-opening on navigation
            const newParams = new URLSearchParams(calendarSearchParams);
            newParams.delete('pendingTime');
            setCalendarSearchParams(newParams, { replace: true });
        }
    }, [calendarSearchParams, selectedTrainer]);

    // Notify parent when week changes
    useEffect(() => {
        if (onWeekChange) {
            onWeekChange(currentWeekStart);
        }
    }, [currentWeekStart, onWeekChange]);

    // Build events array
    const events = useMemo(() => {
        const eventsList = [];

        // Add confirmed trainings
        confirmedTrainings.forEach((training) => {
            eventsList.push({
                ...training,
                id: training.id,
                type: 'confirmed',
                isConfirmed: true,
            });
        });

        // Add pending bookings
        pendingBookings.forEach((booking) => {
            eventsList.push({
                id: `pending-${booking.slot}`,
                startTime: booking.slot,
                endTime: booking.slotEndTime || dayjs(booking.slot).add(60, 'minute').format('YYYY-MM-DDTHH:mm:ss'),
                bookingCount: booking.bookingCount,
                type: 'pending',
            });
        });

        // Add group trainings
        groupTrainings.forEach((training) => {
            eventsList.push({
                ...training,
                id: training.id,
                type: 'group',
                trainingGroup: true,
            });
        });

        // Add locked slots
        lockedSlots.forEach((lock) => {
            eventsList.push({
                ...lock,
                id: lock.id,
                type: 'locked',
                isLocked: true,
            });
        });

        // Add temporary lock slots when in lock mode
        if (isLockMode && selectedLockSlots.length > 0) {
            selectedLockSlots.forEach((slot, index) => {
                eventsList.push({
                    id: `temp-lock-${index}`,
                    startTime: slot.startTime,
                    endTime: slot.endTime,
                    type: 'temporary-lock',
                    isLocked: true,
                    isTemporary: true,
                    tempIndex: index,
                });
            });
        }

        return eventsList;
    }, [confirmedTrainings, pendingBookings, groupTrainings, lockedSlots, isLockMode, selectedLockSlots]);

    // Get event variant for styling
    const getEventVariant = useCallback((event) => {
        if (event.bookingCount && event.bookingCount > 0) return 'pending';
        if (event.isTemporary) return 'temporary-lock';
        if (event.confirmed && event.dogDTO) return 'confirmed';
        if (event.trainingGroup) return 'group';
        if (event.isLocked) return 'locked';
        return 'free';
    }, []);

    // Handle event click
    const handleEventClick = useCallback((event) => {
        if (event.bookingCount && event.bookingCount > 0) {
            // Open pending requests modal
            setSelectedSlot({
                startTime: dayjs(event.slot || event.startTime),
                endTime: dayjs(event.slotEndTime || event.endTime),
            });
            setPendingModalOpen(true);
        } else if (event.confirmed && event.dogDTO) {
            // Open details modal for confirmed trainings
            setSelectedTrainingDetails(event);
            setDetailsModalOpen(true);
        } else if (event.isLocked && event.isTemporary) {
            // Open edit modal for temporary locks
            const tempIndex = event.tempIndex;
            setTempLockToEdit({
                lockIndex: tempIndex,
                lockData: {
                    ...selectedLockSlots[tempIndex],
                    isTemporary: true,
                },
            });
            setEditTempLockModalOpen(true);
        } else if (event.isLocked && !event.isTemporary) {
            // Open edit modal for permanent locks
            setLockToEdit(event);
            setEditLockModalOpen(true);
        } else if (event.trainingGroup) {
            // Open group training details modal
            setSelectedGroupTraining(event);
            setGroupDetailsModalOpen(true);
        }
    }, [selectedLockSlots]);

    // Handle slot click (for creating new bookings or locks)
    const handleSlotClick = useCallback((slotInfo) => {
        console.log('Slot clicked:', slotInfo);

        if (!selectedTrainer) {
            console.log('No trainer selected, returning');
            return;
        }

        const startTime = dayjs(slotInfo.date)
            .hour(parseInt(slotInfo.time.split(':')[0]))
            .minute(parseInt(slotInfo.time.split(':')[1]))
            .second(0);

        console.log('Start time calculated:', startTime.format('YYYY-MM-DD HH:mm'));

        // Check if the slot is within working hours
        const dayOfWeekMapping = {
            1: 'MONDAY', 2: 'TUESDAY', 3: 'WEDNESDAY',
            4: 'THURSDAY', 5: 'FRIDAY', 6: 'SATURDAY', 7: 'SUNDAY',
        };
        const dayOfWeek = dayOfWeekMapping[startTime.isoWeekday()];
        const dayHours = workingHours.find((wh) => wh.dayOfWeek === dayOfWeek);

        // If no working hours for this day, don't allow action
        if (!dayHours) {
            console.log('No working hours for this day');
            alert(t('noWorkingHoursDay'));
            return;
        }

        // Parse working hours times
        const [openHour, openMin] = dayHours.openingTime.split(':').map(Number);
        const [closeHour, closeMin] = dayHours.closingTime.split(':').map(Number);
        const dayStartTime = startTime.clone().hour(openHour).minute(openMin).second(0);
        const dayEndTime = startTime.clone().hour(closeHour).minute(closeMin).second(0);

        // Check if clicked time is within working hours
        if (startTime.isBefore(dayStartTime) || startTime.isSameOrAfter(dayEndTime)) {
            console.log('Slot is outside working hours');
            alert(t('trainerCal.outsideWorkingHours'));
            return;
        }

        if (isLockMode) {
            // Create temporary lock
            const endTime = startTime.clone().add(60, 'minute');

            // Check working hours bounds
            const dayOfWeekMapping = {
                1: 'MONDAY', 2: 'TUESDAY', 3: 'WEDNESDAY',
                4: 'THURSDAY', 5: 'FRIDAY', 6: 'SATURDAY', 7: 'SUNDAY',
            };
            const dayOfWeek = dayOfWeekMapping[startTime.isoWeekday()];
            const dayHours = workingHours.find((wh) => wh.dayOfWeek === dayOfWeek);

            let finalEndTime = endTime;
            if (dayHours) {
                const [closeHour, closeMin] = dayHours.closingTime.split(':').map(Number);
                const dayEndTime = startTime.clone().hour(closeHour).minute(closeMin);
                if (finalEndTime.isAfter(dayEndTime)) {
                    finalEndTime = dayEndTime;
                }
            }

            const newLock = {
                startTime: startTime.format('YYYY-MM-DDTHH:mm:ss'),
                endTime: finalEndTime.format('YYYY-MM-DDTHH:mm:ss'),
                trainerId: selectedTrainer.id,
            };

            const newIndex = selectedLockSlots.length;
            setSelectedLockSlots((prev) => [...prev, newLock]);

            // Open edit modal immediately
            setTempLockToEdit({
                lockIndex: newIndex,
                lockData: { ...newLock, isTemporary: true },
            });
            setEditTempLockModalOpen(true);
        } else {
            // Open booking modal
            const endTime = startTime.clone().add(60, 'minute');
            setSelectedBookingSlot({
                startTime: startTime.clone(),
                endTime: endTime,
            });
            setBookingModalOpen(true);
        }
    }, [selectedTrainer, isLockMode, workingHours, selectedLockSlots, setSelectedLockSlots]);

    // Handle edit training
    const handleEditTraining = useCallback((training) => {
        setTrainingToEdit(training);
        setEditModalOpen(true);
    }, []);

    // Handle delete training
    const handleDeleteTraining = useCallback((training) => {
        setTrainingToDelete(training);
        setDeleteModalOpen(true);
    }, []);

    // Handle delete confirmation
    const handleDeleteConfirm = useCallback(async () => {
        if (!trainingToDelete) return;

        let result;
        if (trainingToDelete.isLocked) {
            result = await deleteLock(trainingToDelete.id);
            if (result.success) {
                alert(t('trainerCal.lockDeletedSuccess'));
            } else {
                alert(t('trainerCal.lockDeleteError'));
            }
        } else {
            result = await deleteTraining(trainingToDelete.id);
            if (result.success) {
                alert(t('trainerCal.trainingDeletedSuccess'));
            } else {
                alert(t('trainerCal.trainingDeleteError'));
            }
        }

        if (result.success) {
            setDeleteModalOpen(false);
            setTrainingToDelete(null);
        }
    }, [trainingToDelete, deleteTraining, deleteLock]);

    // Handle temporary lock editing
    const handleEditTemporaryLock = useCallback((lockIndex, event) => {
        setTempLockToEdit({
            lockIndex,
            lockData: {
                ...selectedLockSlots[lockIndex],
                isTemporary: true,
            },
        });
        setEditTempLockModalOpen(true);
    }, [selectedLockSlots]);

    // Handle temporary lock save
    const handleSaveTemporaryLock = useCallback((updatedLock) => {
        if (tempLockToEdit === null) return;

        const updatedLocks = [...selectedLockSlots];
        updatedLocks[tempLockToEdit.lockIndex] = {
            startTime: updatedLock.startTime,
            endTime: updatedLock.endTime,
            trainerId: selectedTrainer?.id,
        };
        setSelectedLockSlots(updatedLocks);
        setEditTempLockModalOpen(false);
        setTempLockToEdit(null);
    }, [tempLockToEdit, selectedLockSlots, setSelectedLockSlots, selectedTrainer]);

    // Handle temporary lock deletion
    const handleDeleteTemporaryLock = useCallback((lockIndex) => {
        const updatedLocks = [...selectedLockSlots];
        updatedLocks.splice(lockIndex, 1);
        setSelectedLockSlots(updatedLocks);
    }, [selectedLockSlots, setSelectedLockSlots]);

    // Event days for mobile view
    const eventDays = useMemo(() => {
        return events
            .map((e) => dayjs(e.startTime).format('YYYY-MM-DD'))
            .filter((v, i, a) => a.indexOf(v) === i);
    }, [events]);

    const isReady = !!selectedTrainer;

    // Custom event renderer
    const renderEvent = useCallback((event) => {
        const variant = getEventVariant(event);

        // Determine display title
        let displayTitle = '';
        if (event.bookingCount && event.bookingCount > 0) {
            displayTitle = t('trainerCal.pendingCount', { count: event.bookingCount });
        } else if (event.confirmed && event.dogDTO) {
            displayTitle = event.dogDTO.name;
        } else if (event.trainingGroup) {
            displayTitle = event.trainingGroup.name || t('groupTraining');
        } else if (event.isLocked) {
            displayTitle = event.isTemporary ? t('lock.newLock') : t('lock.locked');
        }

        return (
            <CalendarEvent
                key={event.id || event.slotId || `${event.startTime}-${event.endTime}`}
                event={{ ...event, displayTitle }}
                variant={variant}
                position={event.position}
                overlap={event.overlap}
                onClick={() => handleEventClick(event)}
            />
        );
    }, [getEventVariant, handleEventClick]);

    // Mobile view
    if (isMobileView) {
        return (
            <>
                <TrainerCalendarControls
                    trainers={trainers}
                    selectedTrainerId={selectedTrainer?.id}
                    onTrainerChange={onTrainerChange}
                    isLockMode={isLockMode}
                    selectedLockSlotsCount={selectedLockSlots.length}
                    onLockModeToggle={onLockModeToggle}
                    onSaveLock={onSaveLock}
                    onCancelLock={onCancelLock}
                    isMobile={true}
                />

                {isReady ? (
                    <>
                        <Calendar.MobileNavigation />
                        <Calendar.MobileWeekStrip
                            selectedDay={selectedDay}
                            onDaySelect={setSelectedDay}
                            eventDays={eventDays}
                        />
                        <Calendar.MobileDayView
                            selectedDay={selectedDay}
                            events={events}
                            onEventClick={handleEventClick}
                            onSlotClick={handleSlotClick}
                        />
                    </>
                ) : (
                    <SelectionPlaceholder isMobileView={true} />
                )}

                {isLockMode && (
                    <LockModeBar
                        selectedSlots={selectedLockSlots}
                        onClearSelection={() => setSelectedLockSlots([])}
                        onSaveLock={onSaveLock}
                        isSaving={false}
                    />
                )}

                {/* Modals */}
                {renderModals()}
            </>
        );
    }

    // Render modals function
    function renderModals() {
        return (
            <>
                {pendingModalOpen && (
                    <PendingRequestsModal
                        trainerId={selectedTrainer?.id}
                        schoolId={selectedTrainer?.schoolId}
                        slotTime={selectedSlot?.startTime}
                        slotEndTime={selectedSlot?.endTime}
                        onClose={() => setPendingModalOpen(false)}
                        refreshCalendar={refreshCalendar}
                        allowReserveList={schoolSettings?.allowReserveList || false}
                    />
                )}

                {bookingModalOpen && selectedTrainer && (
                    <BookTrainingModal
                        isOpen={bookingModalOpen}
                        onClose={() => setBookingModalOpen(false)}
                        refreshCalendar={refreshCalendar}
                        trainerId={selectedTrainer.id}
                        schoolId={selectedTrainer.schoolId}
                        slotTime={selectedBookingSlot?.startTime}
                        endTime={selectedBookingSlot?.endTime}
                        services={services}
                        halls={halls}
                    />
                )}

                {editModalOpen && trainingToEdit && selectedTrainer && (
                    <EditTrainingModal
                        isOpen={editModalOpen}
                        onClose={() => setEditModalOpen(false)}
                        schoolId={selectedTrainer.schoolId}
                        trainingId={trainingToEdit.id}
                        refreshCalendar={refreshCalendar}
                    />
                )}

                {deleteModalOpen && trainingToDelete && (
                    <DeleteConfirmationModal
                        isOpen={deleteModalOpen}
                        onClose={() => {
                            setDeleteModalOpen(false);
                            setTrainingToDelete(null);
                        }}
                        onConfirm={handleDeleteConfirm}
                        itemName={
                            trainingToDelete.isLocked
                                ? t('trainerCal.lockedSlotItem', { time: `${dayjs(trainingToDelete.startTime).format('HH:mm')} - ${dayjs(trainingToDelete.endTime).format('HH:mm')}` })
                                : t('trainerCal.trainingItem', { time: `${dayjs(trainingToDelete.startTime).format('HH:mm')} - ${dayjs(trainingToDelete.endTime).format('HH:mm')}`, name: trainingToDelete.dogDTO?.name || t('trainerCal.unknown') })
                        }
                    />
                )}

                {detailsModalOpen && selectedTrainingDetails && (
                    <TrainingDetailsModal
                        isOpen={detailsModalOpen}
                        onClose={() => {
                            setDetailsModalOpen(false);
                            setSelectedTrainingDetails(null);
                        }}
                        training={selectedTrainingDetails}
                        onEdit={handleEditTraining}
                        onDelete={handleDeleteTraining}
                    />
                )}

                {groupDetailsModalOpen && selectedGroupTraining && (
                    <GroupTrainingDetailsModal
                        isOpen={groupDetailsModalOpen}
                        onClose={() => {
                            setGroupDetailsModalOpen(false);
                            setSelectedGroupTraining(null);
                        }}
                        training={selectedGroupTraining}
                    />
                )}

                {editLockModalOpen && lockToEdit && (
                    <EditTrainingLockModal
                        isOpen={editLockModalOpen}
                        onClose={() => {
                            setEditLockModalOpen(false);
                            setLockToEdit(null);
                        }}
                        lock={lockToEdit}
                        refreshCalendar={refreshCalendar}
                        onDelete={handleDeleteTraining}
                    />
                )}

                {editTempLockModalOpen && tempLockToEdit && (
                    <EditTemporaryLockModal
                        isOpen={editTempLockModalOpen}
                        onClose={() => {
                            setEditTempLockModalOpen(false);
                            setTempLockToEdit(null);
                        }}
                        lock={tempLockToEdit.lockData}
                        onSave={handleSaveTemporaryLock}
                    />
                )}
            </>
        );
    }

    // Desktop view
    return (
        <>
            <TrainerCalendarControls
                trainers={trainers}
                selectedTrainerId={selectedTrainer?.id}
                onTrainerChange={onTrainerChange}
                isLockMode={isLockMode}
                selectedLockSlotsCount={selectedLockSlots.length}
                onLockModeToggle={onLockModeToggle}
                onSaveLock={onSaveLock}
                onCancelLock={onCancelLock}
            />

            {/* Navigation */}
            <div className="calendar-navigation">
                <div className="week-navigation-left">
                    <button onClick={goToPrevWeek}>‹</button>
                </div>
                <div className="current-week-info">
                    {currentWeekStart.format('DD MMM')} - {currentWeekStart.add(6, 'day').format('DD MMM YYYY')}
                </div>
                <div className="week-navigation-right">
                    <button onClick={goToNextWeek}>›</button>
                </div>
            </div>

            {/* Calendar Grid */}
            {isLoading ? (
                <div className="calendar-loading" style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '400px',
                    fontSize: '16px',
                    color: '#6b7280',
                }}>
                    {t('trainerCal.loadingCalendar')}
                </div>
            ) : isReady ? (
                <Calendar.Grid
                    events={events}
                    renderEvent={renderEvent}
                    onEventClick={handleEventClick}
                    onSlotClick={handleSlotClick}
                    showCurrentTime={true}
                />
            ) : (
                <SelectionPlaceholder isMobileView={false} />
            )}

            {isLockMode && (
                <LockModeBar
                    selectedSlots={selectedLockSlots}
                    onClearSelection={() => setSelectedLockSlots([])}
                    onSaveLock={onSaveLock}
                    isSaving={false}
                />
            )}

            {/* Modals */}
            {renderModals()}
        </>
    );
};

// ============================================
// Main Trainer Calendar Component (Inner)
// ============================================
const TrainerCalendarInner = () => {
    const { t } = useTranslation('calendar');
    const { trainer } = useContext(TrainerContext);
    const [searchParams] = useSearchParams();

    const [selectedTrainer, setSelectedTrainer] = useState(null);
    const [currentWeekStart, setCurrentWeekStart] = useState(() => dayjs().startOf('isoWeek'));
    const [isLockMode, setIsLockMode] = useState(false);
    const [selectedLockSlots, setSelectedLockSlots] = useState([]);

    // Use calendar data hook
    const {
        trainers,
        services,
        halls,
        workingHours,
        confirmedTrainings,
        pendingBookings,
        groupTrainings,
        lockedSlots,
        schoolSettings,
        isLoading,
        refreshCalendar,
        deleteTraining,
        deleteLock,
        lockTimeSlots,
    } = useCalendarData({
        trainerId: selectedTrainer?.id,
        schoolId: trainer?.schoolId,
        weekStart: currentWeekStart,
    });

    // Handle URL params for trainer selection
    useEffect(() => {
        const trainerIdParam = searchParams.get('trainerId');
        if (trainerIdParam && trainers.length > 0) {
            const found = trainers.find((t) => t.id === trainerIdParam);
            if (found) {
                setSelectedTrainer(found);
            }
        }
    }, [searchParams, trainers]);

    // Auto-select logged-in trainer immediately from context
    useEffect(() => {
        if (trainer && !selectedTrainer) {
            setSelectedTrainer(trainer);
        }
    }, [trainer, selectedTrainer]);

    // Update selected trainer with full data from trainers list when available
    useEffect(() => {
        if (trainers.length > 0 && selectedTrainer?.id) {
            const fullTrainerData = trainers.find((t) => t.id === selectedTrainer.id);
            if (fullTrainerData && fullTrainerData !== selectedTrainer) {
                setSelectedTrainer(fullTrainerData);
            }
        }
    }, [trainers, selectedTrainer]);

    // Handle trainer change
    const handleTrainerChange = useCallback((trainerId) => {
        const found = trainers.find((t) => t.id === trainerId);
        setSelectedTrainer(found || null);
    }, [trainers]);

    // Handle week change from calendar navigation
    const handleWeekChange = useCallback((weekStart) => {
        setCurrentWeekStart(weekStart);
    }, []);

    // Lock mode handlers
    const handleLockModeToggle = useCallback(() => {
        setIsLockMode(true);
        setSelectedLockSlots([]);
    }, []);

    const handleSaveLock = useCallback(async () => {
        if (!selectedTrainer?.id || selectedLockSlots.length === 0) return;

        const trainingLockTimes = selectedLockSlots.map((slot) => ({
            startTime: slot.startTime,
            endTime: slot.endTime,
        }));

        const result = await lockTimeSlots({
            trainerId: selectedTrainer.id,
            trainingLockTimes,
        });

        if (result.success) {
            alert(t('lock.locksSavedSuccess'));
            setIsLockMode(false);
            setSelectedLockSlots([]);
        } else {
            alert(t('lock.locksSaveError'));
        }
    }, [selectedTrainer?.id, selectedLockSlots, lockTimeSlots]);

    const handleCancelLock = useCallback(() => {
        setIsLockMode(false);
        setSelectedLockSlots([]);
    }, []);

    return (
        <TrainerLayout noPadding>
            <div className="trainer-calendar-container">
                <div className="calendar-header">
                    <h1 className="calendar-title">{t('trainerCal.title')}</h1>
                </div>

                <Calendar
                    trainerId={selectedTrainer?.id}
                    schoolId={trainer?.schoolId}
                    workingHours={workingHours}
                    basePath="/trainer/calendar"
                    multiSelect={isLockMode}
                    maxSelections={50}
                    features={{
                        slotSelection: true,
                        showCurrentTime: true,
                    }}
                >
                    <TrainerCalendarContent
                        // Data
                        confirmedTrainings={confirmedTrainings}
                        pendingBookings={pendingBookings}
                        groupTrainings={groupTrainings}
                        lockedSlots={lockedSlots}
                        trainers={trainers}
                        services={services}
                        halls={halls}
                        workingHours={workingHours}
                        schoolSettings={schoolSettings}
                        // Selected state
                        selectedTrainer={selectedTrainer}
                        // Handlers
                        onTrainerChange={handleTrainerChange}
                        onWeekChange={handleWeekChange}
                        // Lock mode
                        isLockMode={isLockMode}
                        selectedLockSlots={selectedLockSlots}
                        setSelectedLockSlots={setSelectedLockSlots}
                        onLockModeToggle={handleLockModeToggle}
                        onSaveLock={handleSaveLock}
                        onCancelLock={handleCancelLock}
                        // Refresh
                        refreshCalendar={refreshCalendar}
                        // Actions
                        deleteTraining={deleteTraining}
                        deleteLock={deleteLock}
                        lockTimeSlots={lockTimeSlots}
                        // Loading
                        isLoading={isLoading}
                    />
                </Calendar>
            </div>
        </TrainerLayout>
    );
};

// ============================================
// Exported Component with Legacy Provider
// ============================================
const TrainerCalendar = () => {
    return (
        <LegacyCalendarProvider>
            <TrainerCalendarInner />
        </LegacyCalendarProvider>
    );
};

export default TrainerCalendar;